import { useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import ConsentForm from "@components/ConsentForm";
import ConsentFormFax from "@components/ConsentFormFax";
import styles from "@components/ConsentFormSection.module.css";
import ConsentFormUploader from "@components/forms/ConsentFormUploader";
import ConsentFormWizard from "@components/forms/ConsentFormWizard";
import BodyCopy from "@components/typography/BodyCopy";
import useSubscribeToJourneySignedDocuments from "@hooks/subscriptions/useSubscribeToJourneySignedDocuments";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import {
  GetJourneyWithSignedDocumentsDocument,
  GetJourneyWithSignedDocumentsQuery,
  GetJourneyWithSignedDocumentsQueryVariables,
  SignedDocumentStatusEnum
} from "@typing/Generated";
import { sortByTimestamp } from "@utils/modelUtils";

type Props = {
  disabled?: boolean;
  journeyId: string;
};

const ConsentFormSection = ({ disabled = false, journeyId }: Props) => {
  const myExpert = useMyExpert();

  const [currentFaxFormId, setCurrentFaxFormId] = useState<string | null>(null);

  const signConsentFormOpener = useOpener();
  const uploadConsentFormOpener = useOpener();
  const faxOpener = useOpener();

  const getJourneyWithSignedDocumentsQuery = useQuery<
    GetJourneyWithSignedDocumentsQuery,
    GetJourneyWithSignedDocumentsQueryVariables
  >(GetJourneyWithSignedDocumentsDocument, {
    variables: {
      id: journeyId
    }
  });

  useSubscribeToJourneySignedDocuments(journeyId, () => {
    getJourneyWithSignedDocumentsQuery.refetch();
  });

  const sortedSignedDocuments = useMemo(
    () =>
      sortByTimestamp(
        getJourneyWithSignedDocumentsQuery.data?.journey.signedDocuments ?? [],
        document => document.createdAt
      ),
    [getJourneyWithSignedDocumentsQuery.data?.journey.signedDocuments]
  );

  return (
    <div className={styles.container}>
      <IonGrid className="ion-no-margin ion-no-padding">
        <IonRow>
          <IonCol className="ion-no-margin ion-no-padding">
            <div className={styles.consentFormDescription}>
              <BodyCopy color="gray-500">
                <FormattedMessage id="pages.documents.consentFormDescription" />
              </BodyCopy>
            </div>
            {!myExpert && (
              <PrimaryButton
                disabled={disabled}
                onClick={signConsentFormOpener.open}
                textKey="pages.documents.signConsentForm"
              />
            )}
            <SecondaryButton
              disabled={disabled}
              icon="upload"
              onClick={uploadConsentFormOpener.open}
              textKey="pages.documents.uploadConsentForm"
            />
          </IonCol>
        </IonRow>
      </IonGrid>
      {!disabled &&
        sortedSignedDocuments.map(document => (
          <div key={document.id}>
            {document.status !== SignedDocumentStatusEnum.EMBED_CREATED && (
              <ConsentForm consentForm={document} faxOpener={faxOpener} setCurrentFaxFormId={setCurrentFaxFormId} />
            )}
          </div>
        ))}
      <ConsentFormFax
        currentFaxFormId={currentFaxFormId}
        journey={getJourneyWithSignedDocumentsQuery.data?.journey}
        opener={faxOpener}
      />
      <ConsentFormWizard
        journeyId={journeyId}
        onFormMaybeCompleted={() => getJourneyWithSignedDocumentsQuery.refetch()}
        opener={signConsentFormOpener}
      />
      <ConsentFormUploader journeyId={journeyId} opener={uploadConsentFormOpener} />
    </div>
  );
};

export default ConsentFormSection;
